import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Minus, Plus, CaretLeft, CaretRight, CheckCircle, Flag, MapPin, Target, X, Crown, Scales } from "@phosphor-icons/react";
import { toast } from "sonner";
import { getPendingShots, clearPendingShots, pushPendingShot } from "@/lib/pendingShots";
import RangefinderInline from "@/components/RangefinderInline";
import RulesCheatSheet from "@/components/RulesCheatSheet";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription } from "@/components/ui/sheet";
import { motion, AnimatePresence } from "framer-motion";

const CLUBS = ["Driver","3 Wood","5 Wood","Hybrid","3 Iron","4 Iron","5 Iron","6 Iron","7 Iron","8 Iron","9 Iron","PW","GW","SW","LW","Putter"];

// Haversine yards (mirror of backend) — used to preview shot distance live.
function haversineYards(la1, lo1, la2, lo2) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const dLat = toRad(la2 - la1);
  const dLng = toRad(lo2 - lo1);
  const a = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(la1)) * Math.cos(toRad(la2)) * Math.sin(dLng / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)) * 1.0936133);
}

// Local replica of backend WHS math, for live display.
function courseHandicap(idx, slope, rating, par) {
  if (idx == null) return null;
  return Math.round(idx * (slope / 113) + (rating - par));
}
function strokesPerHole(holes, ph) {
  if (ph == null) return holes.map(() => 0);
  const base = Math.floor(ph / 18);
  const extra = ph % 18;
  return holes.map((h) => base + (h.handicap <= extra ? 1 : 0));
}
function stablefordPoint(strokes, par, sr) {
  const diff = strokes - sr - par;
  if (diff >= 2) return 0;
  if (diff === 1) return 1;
  if (diff === 0) return 2;
  if (diff === -1) return 3;
  if (diff === -2) return 4;
  if (diff === -3) return 5;
  return 6;
}

export default function ScoreEntry() {
  const { courseId } = useParams();
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [course, setCourse] = useState(null);
  const [teeName, setTeeName] = useState(search.get("tee") || "Blue");
  // Optional declared score goal for the round. Parsed from `?target=80` etc;
  // null when no goal set. Drives the on-pace chip + the goal-aware Smart
  // Club Pick on the rangefinder.
  const targetScore = useMemo(() => {
    const t = Number(search.get("target"));
    return Number.isFinite(t) && t > 0 ? t : null;
  }, [search]);
  const [currentHole, setCurrentHole] = useState(0);
  // Per-hole view: "range" = rangefinder map (default during play), "score" =
  // scorecard input. Resets to "range" on every hole change so the player
  // always lands on the map for the new hole.
  const [holePane, setHolePane] = useState("range");
  // Direction tracking for the swipe-deck animation. +1 = slide in from the
  // right (advancing), -1 = slide in from the left (going back). Updated by
  // every code path that mutates `currentHole` so the framer-motion variants
  // pick the right enter/exit transform.
  const [slideDir, setSlideDir] = useState(0);
  const goToHole = (next, dir) => {
    if (next === currentHole) {
      setHolePane("range");
      return;
    }
    setSlideDir(dir ?? (next > currentHole ? 1 : -1));
    setCurrentHole(next);
    setHolePane("range");  // always land on the rangefinder for the new hole
  };
  const [holes, setHoles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [distance, setDistance] = useState(null); // { yards, available }
  // Shot tracker — Pro only. Stored in local state per-hole then batch-uploaded on submit.
  // shots: array of {hole, club, lat, lng}
  const [shots, setShots] = useState([]);
  const [selectedClub, setSelectedClub] = useState("Driver");
  const [logging, setLogging] = useState(false);
  // AI Shot Snap (Bula Pro): when on, every tap of the "+" strokes button
  // auto-records your current GPS as a shot landing for the current hole, using
  // the currently-selected club. No extra taps required. Stored in localStorage
  // so the preference persists across rounds.
  const [aiSnap, setAiSnap] = useState(() => {
    try { return localStorage.getItem("bula.aiSnap") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("bula.aiSnap", aiSnap ? "1" : "0"); } catch {}
  }, [aiSnap]);

  // GPS distance to green: fetch when hole changes if course has any green coords
  useEffect(() => {
    setDistance(null);
    if (!course || !navigator.geolocation) return;
    let cancelled = false;
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        try {
          const { data } = await api.get(
            `/courses/${courseId}/distance/${currentHole + 1}?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`
          );
          if (!cancelled) setDistance(data);
        } catch { /* silent */ }
      },
      () => { /* permission denied / no fix — silent */ },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 30000 }
    );
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentHole, courseId, course?.id]);

  useEffect(() => {
    api.get(`/courses/${courseId}`).then((r) => {
      setCourse(r.data);
      setHoles(r.data.holes.map((h) => ({
        number: h.number, par: h.par, strokes: h.par,
        putts: null, fairway_hit: null, gir: null,
      })));
      if (!r.data.tees.find((t) => t.name === teeName)) {
        setTeeName(r.data.tees[1]?.name || r.data.tees[0]?.name);
      }
      // Hydrate any shots the player saved via the Flyover rangefinder
      // before they reopened the score card — Pro feature.
      if (user?.is_pro) {
        const pending = getPendingShots(courseId);
        if (pending.length) {
          setShots(pending);
          toast.success(`${pending.length} shot${pending.length === 1 ? "" : "s"} restored from your Flyover sessions`);
        }
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  const tee = course?.tees.find((t) => t.name === teeName) || course?.tees[0];

  const ch = useMemo(() => {
    if (!course || !tee) return null;
    return courseHandicap(user?.handicap_index, tee.slope_rating, tee.course_rating, course.par);
  }, [course, tee, user?.handicap_index]);
  const sr = useMemo(() => (course ? strokesPerHole(course.holes, ch) : []), [course, ch]);

  const totals = useMemo(() => {
    const strokes = holes.reduce((a, h) => a + (h.strokes || 0), 0);
    const par = holes.reduce((a, h) => a + h.par, 0);
    const net = holes.reduce((a, h, i) => a + (h.strokes - (sr[i] || 0)), 0);
    const sf = holes.reduce((a, h, i) => a + stablefordPoint(h.strokes, h.par, sr[i] || 0), 0);
    return { strokes, par, diff: strokes - par, net, sf };
  }, [holes, sr]);

  // Goal-round pace projection: extrapolate the player's strokes-over-par
  // for completed holes across all 18 to predict the finishing total. A hole
  // is "complete" once it has a strokes value > 0.
  const pace = useMemo(() => {
    if (!targetScore) return null;
    const completed = holes.filter((h) => (h.strokes || 0) > 0);
    if (completed.length === 0) {
      // No data yet — show the target but no projection.
      return { projected: null, delta: null, holesPlayed: 0, onPace: true };
    }
    const playedStrokes = completed.reduce((a, h) => a + h.strokes, 0);
    const playedPar = completed.reduce((a, h) => a + h.par, 0);
    const overParPerHole = (playedStrokes - playedPar) / completed.length;
    const remaining = holes.slice(completed.length);
    const remainingPar = remaining.reduce((a, h) => a + h.par, 0);
    const projectedRemaining = Math.round(remainingPar + overParPerHole * remaining.length);
    const projected = playedStrokes + projectedRemaining;
    return {
      projected,
      delta: projected - targetScore,  // negative = ahead of goal
      holesPlayed: completed.length,
      onPace: projected <= targetScore,
    };
  }, [holes, targetScore]);

  // Shots logged on the current hole, with live distance to previous-shot preview.
  const currentHoleShots = useMemo(() => {
    const out = [];
    let prev = null;
    shots.forEach((sh, gIdx) => {
      if (sh.hole !== currentHole + 1) return;
      const d = prev ? haversineYards(prev.lat, prev.lng, sh.lat, sh.lng) : null;
      out.push({ ...sh, gIdx, prevDistance: d });
      prev = sh;
    });
    return out;
  }, [shots, currentHole]);

  // Live shot-by-shot satellite is now always visible (side-by-side layout)
  // so we no longer need the collapsible toggle. State left as a no-op for
  // backwards-compat with any tests that flipped it.

  if (!course || !tee) return <div className="py-10">Loading…</div>;
  const h = holes[currentHole];
  const set = (patch) => setHoles((arr) => arr.map((x, i) => (i === currentHole ? { ...x, ...patch } : x)));
  const holeSR = sr[currentHole] || 0;
  const holePts = stablefordPoint(h.strokes, h.par, holeSR);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/rounds", {
        course_id: courseId, tee_name: teeName, holes,
        target_score: targetScore,
      });
      // If Pro user logged any shots, push them to backend tied to this new round.
      if (user?.is_pro && shots.length) {
        for (const sh of shots) {
          try {
            await api.post(`/rounds/${data.id}/shots`, {
              hole: sh.hole, lat: sh.lat, lng: sh.lng, club: sh.club,
            });
          } catch { /* one shot failure shouldn't kill the round save */ }
        }
      }
      // Pending shots (from Flyover rangefinder) are now committed — clear them
      // so the next round on this course doesn't re-import the same set.
      clearPendingShots(courseId);
      toast.success(`Round saved · Diff ${data.differential > 0 ? "+" : ""}${data.differential} · ${data.total_stableford} pts`);
      // Surface freshly-earned badges to the player right after save. Each
      // badge gets its own toast so a streak of awards reads as a celebration,
      // not a single muddled line.
      (data.new_badges || []).forEach((b, i) => {
        setTimeout(() => toast.success(`Badge earned: ${b.name}`, { description: b.description, duration: 5000 }), 400 + i * 250);
      });
      navigate(`/rounds/${data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save round");
    } finally { setSubmitting(false); }
  };

  // Track a shot for the current hole using the browser's geolocation.
  const trackShot = () => {
    if (!navigator.geolocation) {
      toast.error("Geolocation unavailable");
      return;
    }
    setLogging(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setShots((arr) => [...arr, {
          hole: currentHole + 1, club: selectedClub,
          lat: pos.coords.latitude, lng: pos.coords.longitude,
        }]);
        toast.success(`${selectedClub} shot logged on hole ${currentHole + 1}`);
        setLogging(false);
      },
      (err) => { toast.error(err.message || "Couldn't read location"); setLogging(false); },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
  };
  // AI Shot Snap fires when the user increments the stroke count. We don't
  // block the increment on a slow GPS read — strokes update immediately, and
  // the shot is appended async when GPS resolves. A miss (denied, timeout,
  // putter) is silent (toast only on success).
  const snapShotFromStroke = (nextStrokes) => {
    if (!aiSnap || !user?.is_pro || !navigator.geolocation) return;
    // Skip putts — too many short noise events on the green confuses the bag.
    if (selectedClub === "Putter") return;
    // Don't double-snap when the same club is selected and last shot is very fresh.
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setShots((arr) => [...arr, {
          hole: currentHole + 1, club: selectedClub,
          lat: pos.coords.latitude, lng: pos.coords.longitude,
        }]);
      },
      () => { /* silent on snap miss — user will notice no toast and can hit Log shot */ },
      { enableHighAccuracy: true, timeout: 6000, maximumAge: 4000 },
    );
  };
  const removeShot = (idx) => setShots((arr) => arr.filter((_, i) => i !== idx));

  const scoreLabel = (() => {
    const d = h.strokes - h.par;
    if (h.strokes === 1) return "Hole in One!";
    if (d <= -2) return "Eagle";
    if (d === -1) return "Birdie";
    if (d === 0) return "Par";
    if (d === 1) return "Bogey";
    if (d === 2) return "Double";
    return `+${d}`;
  })();

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <span className="label-eyebrow">{course.name}</span>
          <h1 className="font-heading font-black text-2xl">Hole {h.number} <span className="text-[#0F2537]/40">/ 18</span></h1>
        </div>
        <div className="flex items-center gap-3">
          <Sheet>
            <SheetTrigger asChild>
              <button
                type="button"
                data-testid="open-rules-sheet"
                title="Rules cheat-sheet"
                className="w-10 h-10 rounded-full bg-white border border-[#E2E0D8] text-[#0F2537] hover:bg-[#0F2537] hover:text-white transition flex items-center justify-center shrink-0"
              >
                <Scales size={18} weight="fill" />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col bg-[#F8F7F3] overflow-hidden" data-testid="rules-sheet">
              <SheetHeader className="px-5 pt-5 pb-3 border-b border-[#E2E0D8] bg-white">
                <SheetTitle className="font-heading font-black text-xl text-[#0F2537] flex items-center gap-2">
                  <Scales size={20} weight="fill" className="text-[#E05D3A]" /> Rules cheat-sheet
                </SheetTitle>
                <SheetDescription className="text-xs text-[#0F2537]/60">
                  Simplified — the 90 % you'll hit on course. Tap a topic to expand.
                </SheetDescription>
              </SheetHeader>
              <div className="flex-1 overflow-y-auto">
                <RulesCheatSheet idPrefix="ingame-rule" className="px-5" />
              </div>
            </SheetContent>
          </Sheet>
          <div className="text-right">
            <div className="text-xs label-eyebrow">Total · {totals.sf} pts</div>
            <div className="font-heading font-black text-3xl" data-testid="total-strokes">
              {totals.strokes} <span className="text-[#0F2537]/40 text-lg font-bold">({totals.diff >= 0 ? "+" : ""}{totals.diff})</span>
            </div>
          </div>
        </div>
      </div>

      {/* Tee + Handicap row */}
      <div className="card-default p-3 flex flex-wrap items-center justify-between gap-3" data-testid="tee-ch-bar">
        <div className="flex flex-wrap gap-1.5">
          {course.tees.map((t) => (
            <button key={t.name} onClick={() => setTeeName(t.name)} data-testid={`scoreentry-tee-${t.name.toLowerCase()}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold transition ${
                t.name === teeName ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537]"
              }`}>{t.name}</button>
          ))}
        </div>
        <div className="flex gap-4 text-xs">
          <Pill label="CR" v={tee.course_rating} />
          <Pill label="Slope" v={tee.slope_rating} />
          <Pill label="Course Hcp" v={ch ?? "—"} testid="course-handicap-display" />
        </div>
      </div>

      {/* Goal-round pace chip — only renders when a target score was declared. */}
      {pace && (
        <div
          data-testid="goal-pace-chip"
          className={`card-default p-3 flex items-center gap-3 transition ${
            pace.onPace ? "border-l-4 border-l-[#1E5631]" : "border-l-4 border-l-[#E05D3A]"
          }`}
        >
          <Target weight="fill" size={18} className={pace.onPace ? "text-[#1E5631]" : "text-[#E05D3A]"} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/60">
              Goal · break {targetScore}
            </div>
            {pace.projected != null ? (
              <div className="text-sm font-bold text-[#0F2537]">
                On pace for <span className="tabular-nums">{pace.projected}</span>
                <span className={`ml-2 text-xs ${pace.onPace ? "text-[#1E5631]" : "text-[#E05D3A]"}`}>
                  {pace.onPace
                    ? `${Math.abs(pace.delta)} under target`
                    : `${pace.delta} over target`}
                </span>
              </div>
            ) : (
              <div className="text-sm font-bold text-[#0F2537]/70">
                Score a hole to see your projection.
              </div>
            )}
          </div>
        </div>
      )}

      {/* Hole nav strip */}
      <div className="flex gap-1.5 overflow-x-auto py-1">
        {holes.map((x, i) => {
          const filled = i < currentHole;
          const active = i === currentHole;
          return (
            <button key={i} onClick={() => goToHole(i)} data-testid={`hole-tab-${i+1}`}
              className={`shrink-0 w-9 h-9 rounded-lg text-xs font-bold transition ${
                active ? "bg-[#0F2537] text-white" : filled ? "bg-[#E05D3A]/15 text-[#E05D3A]" : "bg-white border border-[#E2E0D8] text-[#0F2537]/70"
              }`}>
              {x.number}
            </button>
          );
        })}
      </div>

      {/* Per-hole 2-pane swiper. Default pane = "range" (live satellite +
          rangefinder readout). Player swipes RIGHT (or taps "Score") to
          reveal the scorecard pane. Hole navigation lives in the header
          (hole-tab strip) + footer (Prev/Next) + the auto-advance on GIR
          set — those are the only paths that mutate `currentHole` so the
          inner pane swap never conflicts with inter-hole navigation. */}
      <div className="relative overflow-x-clip" data-testid="hole-swipe-deck">
        <AnimatePresence initial={false} mode="popLayout" custom={holePane === "score" ? 1 : -1}>
          <motion.div
            key={`${currentHole}-${holePane}`}
            custom={holePane === "score" ? 1 : -1}
            variants={{
              enter: (d) => ({ x: d > 0 ? "100%" : "-100%", opacity: 0 }),
              center: { x: 0, opacity: 1 },
              exit: (d) => ({ x: d > 0 ? "-100%" : "100%", opacity: 0 }),
            }}
            initial="enter"
            animate="center"
            exit="exit"
            transition={{ x: { type: "spring", stiffness: 360, damping: 36 }, opacity: { duration: 0.18 } }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={0.25}
            // dragDirectionLock prevents Leaflet's internal pan from fighting
            // the framer drag on diagonal touches.
            dragDirectionLock
            onDragEnd={(_, info) => {
              const SWIPE_PX = 80;
              const SWIPE_VELOCITY = 380;
              const dx = info.offset.x;
              const vx = info.velocity.x;
              if (holePane === "range" && (dx < -SWIPE_PX || vx < -SWIPE_VELOCITY)) {
                setHolePane("score"); // swipe LEFT on range → reveal scorecard
              } else if (holePane === "score" && (dx > SWIPE_PX || vx > SWIPE_VELOCITY)) {
                setHolePane("range"); // swipe RIGHT on scorecard → back to map
              }
            }}
            className="space-y-5"
          >

      {holePane === "range" ? (
        // --- RANGEFINDER PANE ---
        // Full-width Leaflet map for the current hole with a draggable target
        // crosshair. The player sees yards-to-pin live, can lock to their GPS,
        // and (Pro) save the current target as a tracked shot for this hole.
        <div className="card-default p-0 overflow-hidden" data-testid="rangefinder-pane">
          <RangefinderInline
            hole={course.holes[currentHole]}
            holeNumber={h.number}
            totalYards={tee?.yards?.[currentHole] || 0}
            par={h.par}
            shots={currentHoleShots}
            canTrack={!!user?.is_pro}
            goalAtRisk={!!(pace && !pace.onPace)}
            onTrackShot={({ lat, lng }) => {
              // Reuse the same pending-shots queue the Flyover writes to so
              // shots tracked here survive a refresh and sync into the round
              // on submit (and also surface in the live shot tracker panel
              // below the scorecard pane).
              setShots((arr) => [...arr, { hole: h.number, lat, lng, club: "Driver" }]);
              pushPendingShot({ courseId, hole: h.number, lat, lng, club: "Driver" });
              toast.success(`Shot saved for hole ${h.number}`);
            }}
            onScore={() => setHolePane("score")}
            onOpenFullFlyover={() => window.open(`/courses/${courseId}/flyover?hole=${h.number}`, "_blank")}
          />
        </div>
      ) : (
        // --- SCORECARD PANE ---
        <>
        {/* Back-to-map chip on top of the scorecard pane */}
        <button
          type="button"
          onClick={() => setHolePane("range")}
          data-testid="back-to-rangefinder"
          className="inline-flex items-center gap-1 text-xs font-bold text-[#0F2537]/60 hover:text-[#E05D3A] transition"
        >
          <CaretLeft size={12} weight="bold" /> Back to map
        </button>

      <div className="card-default p-6 md:p-8 text-center relative overflow-hidden">
        <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[#E05D3A]/8" />
        <div className="flex items-center justify-center gap-2 text-sm font-bold text-[#0F2537]/60">
          <Flag size={14} weight="fill" /> PAR {h.par} · {tee.yards[currentHole]} yds · SI {course.holes[currentHole].handicap}
          {holeSR > 0 && (
            <span className="ml-1 inline-flex items-center gap-0.5">
              {Array.from({ length: holeSR }).map((_, i) => (
                <span key={i} className="text-[#E05D3A]" data-testid={`stroke-dot-${i}`}>●</span>
              ))}
            </span>
          )}
        </div>
        {distance?.available && (
          <div className="mt-2 inline-flex items-center gap-2 text-xs font-bold bg-[#1E5631]/10 text-[#1E5631] px-3 py-1 rounded-full" data-testid="gps-distance">
            <MapPin size={12} weight="fill" />
            {distance.front != null && distance.back != null && distance.front !== distance.back ? (
              <span className="inline-flex items-center gap-2">
                <span data-testid="gps-front">F {distance.front}</span>
                <span className="opacity-50">·</span>
                <span data-testid="gps-center" className="font-extrabold">C {distance.center}</span>
                <span className="opacity-50">·</span>
                <span data-testid="gps-back">B {distance.back}</span>
                <span className="opacity-70">yds</span>
              </span>
            ) : (
              <span>{distance.yards} yds to green</span>
            )}
          </div>
        )}
        <div className="mt-6 flex items-center justify-center gap-6">
          <button onClick={() => set({ strokes: Math.max(1, h.strokes - 1) })} data-testid="strokes-minus" className="score-btn w-16 h-16 flex items-center justify-center text-[#0F2537]">
            <Minus size={28} weight="bold" />
          </button>
          <div>
            <div className="font-heading font-black text-7xl tracking-tighter leading-none" data-testid="strokes-value">{h.strokes}</div>
            <div className="text-sm font-bold text-[#E05D3A] mt-1">
              {scoreLabel}
              {ch != null && <span className="text-[#0F2537]/70"> · Net {h.strokes - holeSR} · {holePts} pt{holePts === 1 ? "" : "s"}</span>}
            </div>
          </div>
          <button onClick={() => { const next = h.strokes + 1; set({ strokes: next }); snapShotFromStroke(next); }} data-testid="strokes-plus" className="score-btn w-16 h-16 flex items-center justify-center text-[#0F2537]">
            <Plus size={28} weight="bold" />
          </button>
        </div>

        <div className="mt-7 grid sm:grid-cols-3 gap-4">
          <div>
            <div className="label-eyebrow mb-2">Putts</div>
            <div className="flex items-center justify-center gap-3">
              <button data-testid="putts-minus" className="score-btn w-10 h-10" onClick={() => set({ putts: Math.max(0, (h.putts ?? 2) - 1) })}><Minus size={16} weight="bold" /></button>
              <div className="font-heading font-bold text-2xl w-8" data-testid="putts-value">{h.putts ?? "—"}</div>
              <button data-testid="putts-plus" className="score-btn w-10 h-10" onClick={() => set({ putts: (h.putts ?? 2) + 1 })}><Plus size={16} weight="bold" /></button>
            </div>
          </div>
          <Toggle label="Fairway hit" testid="fairway" value={h.fairway_hit} onChange={(v) => set({ fairway_hit: v })} disabled={h.par === 3} />
          <Toggle label="Green in reg" testid="gir" value={h.gir} onChange={(v) => {
            set({ gir: v });
            // Auto-advance: GIR is the last "explicit" tap on the scorecard
            // (strokes default to par, putts default to 2 if untouched, FW is
            // par-3-N/A). Once the player decides Yes/No on GIR we treat the
            // hole as "marked" and slide to the next. 900ms delay so the
            // toggle's own pop animation completes and the user feels the
            // confirmation before the deck moves.
            if (v !== null && currentHole < 17) {
              setTimeout(() => goToHole(currentHole + 1, 1), 900);
            }
          }} />
        </div>
      </div>

      {/* Shot tracker — Bula Pro */}
      {user?.is_pro ? (
        <div className="card-default p-4 md:p-5" data-testid="shot-tracker-panel">
          <div className="flex items-center justify-between gap-2 mb-3">
            <div className="flex items-center gap-2">
              <Target size={16} weight="fill" className="text-[#E05D3A]" />
              <span className="label-eyebrow">Shot tracker</span>
            </div>
            <span className="text-[10px] font-bold text-[#0F2537]/50">{shots.length} this round</span>
          </div>
          {/* AI Shot Snap — auto-log a shot every time the strokes counter
              advances on a non-putter club. */}
          <label className="flex items-center justify-between gap-3 px-3 py-2 mb-2 rounded-lg bg-[#E05D3A]/8 cursor-pointer" data-testid="ai-snap-row">
            <span className="flex items-center gap-1.5 text-xs font-bold text-[#0F2537]">
              <Crown size={12} weight="fill" className="text-[#E05D3A]" /> AI Shot Snap
              <span className="text-[10px] font-bold text-[#0F2537]/55 hidden sm:inline">— auto-log GPS when you tap "+"</span>
            </span>
            <input type="checkbox" checked={aiSnap} onChange={(e) => setAiSnap(e.target.checked)}
              data-testid="ai-snap-toggle" className="w-4 h-4 accent-[#E05D3A]" />
          </label>
          <div className="flex flex-col sm:flex-row gap-2">
            <select
              value={selectedClub} onChange={(e) => setSelectedClub(e.target.value)}
              data-testid="shot-club-select"
              className="flex-1 rounded-lg border border-[#E2E0D8] bg-white px-3 py-2 text-sm font-semibold"
            >
              {CLUBS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
            <button
              onClick={trackShot} disabled={logging}
              data-testid="log-shot-btn"
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-[#E05D3A] text-white text-sm font-bold disabled:opacity-50 hover:brightness-110 transition"
            >
              <MapPin size={14} weight="fill" /> {logging ? "Locating…" : `Log ${selectedClub}`}
            </button>
          </div>
          {currentHoleShots.length > 0 && (
            <ul className="mt-3 space-y-1.5">
              {currentHoleShots.map((sh) => (
                <li key={sh.gIdx} data-testid={`shot-row-${sh.gIdx}`} className="flex items-center gap-2 text-sm">
                  <span className="w-6 text-center text-[10px] font-bold text-[#0F2537]/50">#{currentHoleShots.indexOf(sh) + 1}</span>
                  <span className="font-bold flex-1 truncate">{sh.club}</span>
                  {sh.prevDistance != null && (
                    <span className="text-xs font-bold text-[#1E5631] bg-[#1E5631]/10 px-2 py-0.5 rounded-full">
                      +{sh.prevDistance} yds from prev
                    </span>
                  )}
                  <button onClick={() => removeShot(sh.gIdx)} data-testid={`shot-remove-${sh.gIdx}`} className="text-[#0F2537]/40 hover:text-red-600 p-1">
                    <X size={12} weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="mt-3 text-[10px] text-[#0F2537]/50 leading-relaxed">
            Distance is the line between consecutive shots on the same hole. Shots are saved when you submit the round.
          </p>
        </div>
      ) : (
        <div className="card-default p-4 md:p-5 flex items-center justify-between gap-3" data-testid="shot-tracker-paywall">
          <div className="flex items-center gap-2">
            <Crown size={16} weight="fill" className="text-[#E05D3A]" />
            <span className="text-sm font-bold">Track each shot and build your real club bag</span>
          </div>
          <button onClick={() => navigate("/pricing")} data-testid="shot-tracker-go-pro" className="px-3 py-1.5 rounded-lg bg-[#0F2537] text-white text-xs font-bold hover:bg-[#0F2537]/90">
            Go Pro
          </button>
        </div>
      )}

      </>
      )}{/* /holePane === score */}

          </motion.div>
        </AnimatePresence>
      </div>

      <div className="flex items-center justify-between gap-3">
        <button onClick={() => goToHole(Math.max(0, currentHole - 1), -1)} disabled={currentHole === 0} data-testid="prev-hole-btn"
          className="px-4 py-3 rounded-xl border border-[#E2E0D8] bg-white font-semibold inline-flex items-center gap-1 disabled:opacity-40">
          <CaretLeft weight="bold" /> Prev
        </button>
        {currentHole < 17 ? (
          <button onClick={() => goToHole(currentHole + 1, 1)} data-testid="next-hole-btn"
            className="flex-1 px-4 py-3 rounded-xl bg-[#0F2537] text-white font-bold inline-flex items-center justify-center gap-1">
            Next hole <CaretRight weight="bold" />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} data-testid="submit-round-btn"
            className="flex-1 px-4 py-3 rounded-xl bg-[#E05D3A] text-white font-bold inline-flex items-center justify-center gap-1 disabled:opacity-60">
            <CheckCircle weight="fill" /> {submitting ? "Saving…" : "Save round"}
          </button>
        )}
      </div>
    </div>
  );
}

function Pill({ label, v, testid }) {
  return (
    <span className="inline-flex flex-col items-center" data-testid={testid}>
      <span className="text-[10px] uppercase tracking-widest text-[#0F2537]/55 font-bold">{label}</span>
      <span className="font-heading font-bold text-[#0F2537]">{v}</span>
    </span>
  );
}

function Toggle({ label, value, onChange, disabled, testid }) {
  return (
    <div className={disabled ? "opacity-40" : ""}>
      <div className="label-eyebrow mb-2">{label}</div>
      <div className="flex justify-center gap-2">
        <button disabled={disabled} data-testid={`${testid}-yes`} onClick={() => onChange(value === true ? null : true)}
          className={`px-4 py-2 rounded-lg text-sm font-bold border ${value === true ? "bg-[#1E5631] text-white border-[#1E5631]" : "bg-white border-[#E2E0D8] text-[#0F2537]"}`}>Yes</button>
        <button disabled={disabled} data-testid={`${testid}-no`} onClick={() => onChange(value === false ? null : false)}
          className={`px-4 py-2 rounded-lg text-sm font-bold border ${value === false ? "bg-[#0F2537] text-white border-[#0F2537]" : "bg-white border-[#E2E0D8] text-[#0F2537]"}`}>No</button>
      </div>
    </div>
  );
}
